import { useEffect, useState } from 'react';
import { ExternalLink, Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLinkStripeCustomer } from '@/hooks/users/useUserManagement';
import { ProfileWithSubscription } from '@/types/auth/auth';

interface StripeCustomerLinkProps {
  user: ProfileWithSubscription;
}

export const StripeCustomerLink = ({ user }: StripeCustomerLinkProps) => {
  const [customerId, setCustomerId] = useState(user.stripe_id ?? '');
  const linkMutation = useLinkStripeCustomer();
  const trimmed = customerId.trim();
  const isLinked = !!user.stripe_id && trimmed === user.stripe_id;

  useEffect(() => {
    setCustomerId(user.stripe_id ?? '');
  }, [user.stripe_id]);

  const handleLink = () => {
    if (!trimmed) return;
    linkMutation.mutate({ userId: user.id, customerId: trimmed });
  };

  return (
    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
      <div className="flex items-center mb-[18px]">
        <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
          <Link2 className="h-5 w-5 text-genie-primary" />
        </div>
        <h2 className="text-xl font-semibold m-0">Stripe Customer</h2>
      </div>

      <Label htmlFor="stripe-customer-id" className="block mb-1.5 font-medium text-sm">
        Customer ID
      </Label>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          id="stripe-customer-id"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleLink();
            }
          }}
          placeholder="cus_..."
          className="min-h-[44px] text-base sm:text-sm font-mono"
          disabled={linkMutation.isPending}
        />
        <Button
          type="button"
          onClick={handleLink}
          disabled={!trimmed || linkMutation.isPending}
          className="min-h-[44px] bg-genie-primary hover:bg-genie-primary/90 gap-2 shrink-0"
        >
          {linkMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isLinked ? 'Sync from Stripe' : 'Link & sync'}
        </Button>
        {user.stripe_id && (
          <Button
            type="button"
            variant="outline"
            onClick={() => window.open(`https://dashboard.stripe.com/customers/${user.stripe_id}`, '_blank')}
            className="min-h-[44px] gap-2 shrink-0"
          >
            <ExternalLink className="h-4 w-4" />
            Open in Stripe
          </Button>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        For customers sales set up directly in Stripe under a different email. Linking pulls in their
        subscriptions now, and Stripe keeps them in sync from then on.
      </p>
    </div>
  );
};
